import { useId, useState, type FormEvent } from 'react';
import {
  BUTTON_CLASS,
  DetailDrawer,
  fieldStyle,
  neutralButtonStyle,
  primaryButtonStyle,
  StatusDot,
  token,
} from '@bp-files/ui';
import { messageFrom } from '@bp-files/client';
import { SPACE_LIST_COPY } from '@/components/SpaceList/spaceList.copy';
import { renameSpace } from '@/lib/api';

/**
 * Rename a room — one field, one request (CON-22, CON-25, **ADR-0102**).
 *
 * **`CreateSpacePanel` seeded**, and the seeding is the only real difference:
 * a rename is an edit rather than an entry, and asking somebody to retype a
 * name they are correcting one character of is how a typo gets replaced by a
 * different typo. The field takes `spaceName` as its initial value and is not
 * resynchronised afterwards — the caller closes this drawer on success, so
 * there is no later value for it to drift from.
 *
 * **Reached from the row's "⋯", not from the members panel.** Every control in
 * that panel names an agency; this one names none. Delete sits there as a
 * deliberate exception, and only because a room is deleted *after* it has been
 * emptied and the roster is where an owner finds that out. ADR-0102 has the
 * argument, including why a textbox does not go inside `RowActionsMenu` the
 * way the delete confirmation does.
 *
 * **The refusal is the API's own, and the code is the unusual one.** A fellow
 * member's admin gets a **403** rather than the 404 the rest of that route
 * family answers with, because the roster has already disclosed the room's
 * name and its owner to them (CON-11 as amended) — so the message says who may
 * perform the act, which `errors.failed` would throw away. That fallback is
 * for a failure carrying no message at all.
 *
 * Built on `DetailDrawer`, which is already a modal dialog with a focus trap,
 * Escape handling, and focus restore. Nothing here re-implements any of that.
 */

export interface RenameSpacePanelProps {
  open: boolean;
  onClose: () => void;
  /** The room being renamed. Addresses the request. */
  spaceId: string;
  /** Its current name — the field's initial value, never resynchronised. */
  spaceName: string;
  /**
   * Called once the room has been renamed.
   *
   * The caller refetches on this and never patches (§4): a name shows on the
   * table, in the members drawer's title, on the Files breadcrumb and in the
   * recycle bin's origin line, and the server's answer is the only one worth
   * rendering across all of them. No id travels back — unlike creating, there
   * is no new room to hand over to.
   */
  onRenamed: () => void;
}

export function RenameSpacePanel({
  open,
  onClose,
  spaceId,
  spaceName,
  onRenamed,
}: RenameSpacePanelProps) {
  const nameId = useId();
  const nameHintId = useId();

  const [name, setName] = useState(spaceName);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();

  /**
   * `void`-returning, with the async work in a nested function —
   * `CreateSpacePanel`'s rule. A submit handler that returned a promise would
   * be a floating one as far as React is concerned; every failure path below
   * is caught, and the signature says so.
   */
  function submit(event: FormEvent): void {
    event.preventDefault();
    if (pending) return;

    const wanted = name.trim();
    if (wanted === '') {
      setError(SPACE_LIST_COPY.renameSpace.errors.nameRequired);
      return;
    }

    void send(wanted);
  }

  async function send(wanted: string): Promise<void> {
    setPending(true);
    setError(undefined);

    try {
      await renameSpace(spaceId, wanted);
      onRenamed();
    } catch (caught) {
      // The owner clause's 403 lands here, in the API's own wording.
      setError(messageFrom(caught, SPACE_LIST_COPY.renameSpace.errors.failed));
    } finally {
      setPending(false);
    }
  }

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={SPACE_LIST_COPY.renameSpace.title}
      closeLabel={SPACE_LIST_COPY.renameSpace.close}
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        <p className="text-sm" style={{ color: token('bp-text-muted') }}>
          {SPACE_LIST_COPY.renameSpace.intro}
        </p>

        <div className="flex flex-col gap-1.5">
          <label htmlFor={nameId} className="text-xs font-medium">
            {SPACE_LIST_COPY.renameSpace.label}
          </label>
          <input
            id={nameId}
            name="name"
            type="text"
            value={name}
            aria-describedby={nameHintId}
            onChange={(event) => {
              setName(event.target.value);
            }}
            className="border px-3 py-2 text-sm"
            style={fieldStyle()}
          />
          <span id={nameHintId} className="text-xs" style={{ color: token('bp-text-muted') }}>
            {SPACE_LIST_COPY.renameSpace.hint}
          </span>
        </div>

        {error === undefined ? null : (
          <p role="alert" className="flex items-start gap-2 text-sm text-danger">
            <StatusDot tone="alert" />
            {error}
          </p>
        )}

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={pending}
            className={BUTTON_CLASS}
            style={primaryButtonStyle()}
          >
            {pending ? SPACE_LIST_COPY.renameSpace.submitting : SPACE_LIST_COPY.renameSpace.submit}
          </button>

          <button
            type="button"
            onClick={onClose}
            className={BUTTON_CLASS}
            style={neutralButtonStyle()}
          >
            {SPACE_LIST_COPY.renameSpace.cancel}
          </button>
        </div>
      </form>
    </DetailDrawer>
  );
}
